"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu"
import { ClockWidget } from "@/components/widgets/ClockWidget"
import { WeatherWidget } from "@/components/widgets/WeatherWidget"
import { Calendar } from "@/components/ui/calendar"
import { Clock, Calendar as CalendarIcon, Bell, User, Briefcase, Settings, ShoppingBag, Moon, Sun, Languages, LogOut, ChevronDown, Building2, Shield, Terminal, CloudSun, MapPin, FolderOpen, Check, Plus, Home } from "lucide-react"
import { toast } from "sonner"

interface DesktopTopBarProps {
  widgetsConfig?: any[]
  activeAppId: string | null
  theme: string | undefined
  setTheme: (theme: string) => void
  topBarConfig: any
  currentTime: string
  currentDate: string
  weatherInfo: string
  lang: string
  setLang: (lang: string) => void
  t: Record<string, string>
  user: any
  currentAccount: any
  currentWorkspace?: any
  setCurrentWorkspace?: (ws: any) => void
  accounts?: any[]
  notifications: any[]
  showNotificationCenter: boolean
  setShowNotificationCenter: (show: boolean) => void
  clockTab: any
  setClockTab: (tab: any) => void
  alarms: any[]
  setAlarms: React.Dispatch<React.SetStateAction<any[]>>
  newAlarmTime: string
  setNewAlarmTime: (time: string) => void
  newAlarmLabel: string
  setNewAlarmLabel: (label: string) => void
  timerState: any
  setTimerState: React.Dispatch<React.SetStateAction<any>>
  timerMinutes: any
  setTimerMinutes: (min: any) => void
  calendarDate: Date | undefined
  setCalendarDate: (date: Date | undefined) => void
  setIsSwitchAccountOpen: (open: boolean) => void
  setAccountSearchQuery: (query: string) => void
  handleOpenProfileTab: () => void
  setProfileTab: (tab: any) => void
  handleOpenMarketplaceTab?: () => void
  handleOpenSettingsTab: () => void
  setSettingsTab: (tab: any) => void
  handleLogout: () => void
  setActiveAppId: (id: string | null) => void
  profileRole?: string
  setSplitActiveAppId: (id: string | null) => void
}

export function DesktopTopBar({
  widgetsConfig,
  activeAppId,
  theme,
  setTheme,
  topBarConfig,
  currentTime,
  currentDate,
  weatherInfo,
  lang,
  setLang,
  t,
  user,
  currentAccount,
  currentWorkspace,
  setCurrentWorkspace,
  accounts,
  notifications,
  showNotificationCenter,
  setShowNotificationCenter,
  clockTab,
  setClockTab,
  alarms,
  setAlarms,
  newAlarmTime,
  setNewAlarmTime,
  newAlarmLabel,
  setNewAlarmLabel,
  timerState,
  setTimerState,
  timerMinutes,
  setTimerMinutes,
  calendarDate,
  setCalendarDate,
  setIsSwitchAccountOpen,
  setAccountSearchQuery,
  handleOpenProfileTab,
  setProfileTab,
  handleOpenMarketplaceTab,
  handleOpenSettingsTab,
  setSettingsTab,
  handleLogout,
  setActiveAppId,
  profileRole,
  setSplitActiveAppId
}: DesktopTopBarProps) {
  const router = useRouter()
  const isAdmin = profileRole === "admin" || profileRole === "owner"
  const hasCompany = Boolean(currentAccount && accounts && accounts.length > 0)

  return (
    <header className={`relative hidden md:flex h-14 w-full items-center justify-between border-b px-6 transition-all duration-300 z-50 shrink-0 ${activeAppId === null
      ? theme === "light"
        ? "bg-white/60 border-zinc-200/50 backdrop-blur-md shadow-xs"
        : "bg-zinc-950/25 border-white/10 dark:border-zinc-850/10 backdrop-blur-md shadow-none"
      : "border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
      }`}>

      {/* LADO ESQUERDO: Logo com indicador intuitivo de Hub Inicial */}
      <div className="flex items-center gap-6">
        <div
          onClick={() => {
            router.push("/")
          }}
          title="Ir para o Hub Inicial"
          className="group relative flex items-center gap-2.5 py-1 px-2.5 -ml-2.5 rounded-lg cursor-pointer transition-all duration-200 select-none hover:bg-black/5 dark:hover:bg-white/5"
        >
          <img
            src="/logo.png"
            alt="Bold"
            className="size-8 rounded-lg object-cover transition-all duration-300 shrink-0"
          />
          <div className="hidden sm:flex flex-col justify-center text-left">
            <span
              className={`font-bold tracking-tight text-xs md:text-sm leading-tight transition-colors ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-900 group-hover:text-[#004EB1]"
                  : "text-white text-shadow-md group-hover:text-blue-300"
                : "text-zinc-900 dark:text-white group-hover:text-[#004EB1] dark:group-hover:text-blue-400"
                }`}
            >
              Bold Workspace
            </span>
            <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-300 ease-out overflow-hidden">
              <span
                className={`min-h-0 text-[10px] font-semibold tracking-wide flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0 ${activeAppId === null
                  ? theme === "light"
                    ? "text-[#004EB1]"
                    : "text-blue-300"
                  : "text-[#004EB1] dark:text-blue-400"
                  }`}
              >
                Hub Inicial
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* LADO CENTRO: Relógio, Data e Clima com Popovers Interativos */}
      <div className="absolute left-1/2 -translate-x-1/2 hidden lg:flex items-center justify-center gap-3 text-xs select-none h-full">
        {topBarConfig.showClock && (
          <Popover>
            <PopoverTrigger className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border shadow-xs cursor-pointer transition-colors focus:outline-none select-none font-sans ${activeAppId === null
              ? theme === "light"
                ? "bg-zinc-150/70 border-zinc-200/60 text-zinc-800 hover:bg-zinc-200/50"
                : "bg-white/10 border-white/10 text-white hover:bg-white/20"
              : "bg-zinc-50 border-zinc-200/50 text-zinc-500 hover:bg-zinc-100 dark:bg-zinc-800/40 dark:border-zinc-800/80 dark:text-zinc-400 dark:hover:bg-zinc-850/50"
              }`}>
              <Clock className={`size-3.5 ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-600"
                  : "text-blue-300"
                : "text-[#004EB1]"
                }`} />
              <span className={`font-semibold tabular-nums ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-800"
                  : "text-white text-shadow-md"
                : "text-zinc-750 dark:text-zinc-300"
                }`}>{currentTime}</span>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans shadow-xl rounded-xl">
              <ClockWidget
                t={t}
                lang={lang}
                userId={user?.id}
                currentTime={currentTime}
                clockTab={clockTab}
                setClockTab={setClockTab}
                alarms={alarms}
                setAlarms={setAlarms}
                newAlarmTime={newAlarmTime}
                setNewAlarmTime={setNewAlarmTime}
                newAlarmLabel={newAlarmLabel}
                setNewAlarmLabel={setNewAlarmLabel}
                timerSeconds={timerState.remaining}
                setTimerSeconds={(s) => setTimerState((prev: any) => ({ ...prev, remaining: s }))}
                timerActive={timerState.isRunning}
                setTimerActive={(active) => setTimerState((prev: any) => ({ ...prev, isRunning: active }))}
                timerInputMinutes={timerMinutes}
                setTimerInputMinutes={setTimerMinutes}
              />
            </PopoverContent>
          </Popover>
        )}

        {topBarConfig.showDate && (
          <Popover>
            <PopoverTrigger className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border shadow-xs cursor-pointer transition-colors focus:outline-none select-none font-sans ${activeAppId === null
              ? theme === "light"
                ? "bg-zinc-150/70 border-zinc-200/60 text-zinc-800 hover:bg-zinc-200/50"
                : "bg-white/10 border-white/10 text-white hover:bg-white/20"
              : "bg-zinc-50 border-zinc-200/50 text-zinc-500 hover:bg-zinc-100 dark:bg-zinc-800/40 dark:border-zinc-800/80 dark:text-zinc-400 dark:hover:bg-zinc-850/50"
              }`}>
              <CalendarIcon className={`size-3.5 ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-600"
                  : "text-blue-300"
                : "text-[#004EB1]"
                }`} />
              <span className={`capitalize ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-800"
                  : "text-white text-shadow-md"
                : "text-zinc-750 dark:text-zinc-300"
                }`}>{currentDate}</span>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans shadow-xl rounded-xl">
              <Calendar
                mode="single"
                selected={calendarDate}
                onSelect={setCalendarDate}
                className="rounded-md border border-zinc-100 dark:border-zinc-850 bg-white dark:bg-zinc-900"
              />
            </PopoverContent>
          </Popover>
        )}

        {topBarConfig.showWeather && (widgetsConfig ? widgetsConfig.find((w: any) => w.id === "system-weather")?.enabled !== false : true) && (
          <Popover>
            <PopoverTrigger className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border shadow-xs cursor-pointer transition-colors focus:outline-none select-none font-sans ${activeAppId === null
              ? theme === "light"
                ? "bg-zinc-150/70 border-zinc-200/60 text-zinc-800 hover:bg-zinc-200/50"
                : "bg-white/10 border-white/10 text-white hover:bg-white/20"
              : "bg-zinc-50 border-zinc-200/50 text-zinc-500 hover:bg-zinc-100 dark:bg-zinc-800/40 dark:border-zinc-800/80 dark:text-zinc-400 dark:hover:bg-zinc-850/50"
              }`}>
              <CloudSun className={`size-3.5 ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-650"
                  : "text-blue-300"
                : "text-[#004EB1]"
                }`} />
              <span className={`font-medium text-xs ${activeAppId === null
                ? theme === "light"
                  ? "text-zinc-850"
                  : "text-white text-shadow-md"
                : "text-zinc-750 dark:text-zinc-300"
                }`}>
                {weatherInfo.includes("°C") ? (
                  weatherInfo
                ) : (
                  <span className="flex items-center gap-1">
                    <MapPin className="size-3 text-blue-500 inline" />
                    <span>Compartilhar localização</span>
                  </span>
                )}
              </span>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans shadow-xl rounded-xl">
              <WeatherWidget weatherInfo={weatherInfo} lang={lang} />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* CANTO DIREITO: Notificações + Menu Perfil */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowNotificationCenter(!showNotificationCenter)}
          className="flex size-9 items-center justify-center rounded-md relative text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer focus:outline-none"
          title="Central de Notificações"
        >
          <Bell className="size-4.5" />
          {notifications.length > 0 && (
            <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-[#004EB1]" />
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger className="flex h-9 items-center justify-center gap-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xs px-2.5 text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors text-xs font-semibold cursor-pointer focus:outline-none">
            {(() => {
              const logoClass = 'h-7 w-auto object-contain'
              const companyBadgeClass = 'size-7 rounded-lg bg-[#004EB1] text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-xs'
              const userAvatarOverlapClass = 'ring-2 ring-white dark:ring-zinc-900 ml-1'

              const logoUrl = currentAccount?.logoUrl || null
              const companyName = currentAccount?.name || ""
              const companyInitial = companyName ? companyName[0].toUpperCase() : "B"
              const hasCompanyBadge = !!currentAccount

              return (
                <>
                  {hasCompanyBadge && (
                    logoUrl ? (
                      <img src={logoUrl} alt="Logo" className={logoClass} />
                    ) : (
                      <div className={companyBadgeClass} title={companyName}>
                        {companyInitial}
                      </div>
                    )
                  )}
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user.name} className={`size-7 rounded-full object-cover ${hasCompanyBadge ? userAvatarOverlapClass : ""}`} />
                  ) : (
                    <div className={`flex size-7 items-center justify-center rounded-full bg-[#004EB1]/10 text-[#004EB1] dark:bg-blue-955/30 dark:text-blue-400 ${hasCompanyBadge ? userAvatarOverlapClass : ""}`}>
                      <User className="size-4" />
                    </div>
                  )}
                </>
              )
            })()}
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-64 mt-1 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-1 font-sans z-[99999]">
            <DropdownMenuLabel className="font-bold text-zinc-800 dark:text-zinc-200 text-xs px-2 py-1.5">
              {user?.name}
              <span className="block text-[10px] font-normal text-zinc-450 mt-0.5">{user?.email}</span>
            </DropdownMenuLabel>

            <DropdownMenuSeparator className="bg-zinc-100 dark:bg-zinc-800" />

            {hasCompany ? (
              <DropdownMenuItem
                onClick={() => { setIsSwitchAccountOpen(true); setAccountSearchQuery(""); }}
                className="cursor-pointer text-xs py-2 px-2.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2.5 font-sans"
              >
                <Briefcase className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
                <div className="flex flex-col min-w-0 text-left">
                  <span className="font-semibold text-zinc-900 dark:text-white truncate text-xs leading-tight">
                    {currentAccount?.name || "Empresa"}
                  </span>
                  <span className="text-[10px] text-zinc-400 dark:text-zinc-500 font-normal leading-tight mt-0.5">
                    {t.switchAccountTitle || "Trocar de Empresa"}
                  </span>
                </div>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => { setIsSwitchAccountOpen(true); setAccountSearchQuery(""); }}
                className="cursor-pointer text-xs py-2 px-2.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2.5 font-sans font-semibold text-[#004EB1] dark:text-blue-400"
              >
                <Plus className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
                <div className="flex flex-col min-w-0 text-left">
                  <span className="font-semibold text-zinc-900 dark:text-white text-xs leading-tight">
                    Nenhuma Empresa
                  </span>
                  <span className="text-[10px] text-[#004EB1] dark:text-blue-400 font-medium leading-tight mt-0.5">
                    Criar Empresa
                  </span>
                </div>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => { handleOpenProfileTab(); setProfileTab("profile"); }}
              className="cursor-pointer text-xs py-1.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2 font-sans font-semibold"
            >
              <User className="size-4 text-[#004EB1]" />
              {t.myProfileTitle || "Meu Perfil"}
            </DropdownMenuItem>

            {hasCompany && isAdmin && (
              <DropdownMenuItem
                onClick={() => { if (handleOpenMarketplaceTab) handleOpenMarketplaceTab(); }}
                className="cursor-pointer text-xs py-1.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2 font-sans font-semibold"
              >
                <ShoppingBag className="size-4 text-[#004EB1]" />
                {t.appStoreTitle || "Loja de Aplicativos"}
              </DropdownMenuItem>
            )}

            {hasCompany && (
              <DropdownMenuItem
                onClick={() => { handleOpenSettingsTab(); setSettingsTab("company"); }}
                className="cursor-pointer text-xs py-1.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2 font-sans font-semibold"
              >
                <Settings className="size-4 text-[#004EB1]" />
                {t.settingsTitle || "Configurações do Sistema"}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator className="bg-zinc-100 dark:bg-zinc-800" />

            <DropdownMenuLabel className="text-[9px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider px-2 py-1">
              {lang === "pt" ? "Aparência & Idioma" : lang === "es" ? "Apariencia e Idioma" : "Appearance & Language"}
            </DropdownMenuLabel>

            <div className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300 select-none">
              <span className="flex items-center gap-2">
                {theme === "dark" ? <Moon className="size-4 text-purple-500" /> : <Sun className="size-4 text-amber-500 animate-spin-slow" />}
                {t.darkModeLabel || "Modo Escuro"}
              </span>
              <button
                type="button"
                onClick={() => {
                  const nextTheme = theme === "dark" ? "light" : "dark"
                  setTheme(nextTheme)
                  try {
                    const iframes = document.querySelectorAll("iframe")
                    const currentLang = lang || (typeof localStorage !== "undefined" ? localStorage.getItem("language") : null) || "pt"
                    iframes.forEach((iframe) => {
                      try {
                        const win = iframe.contentWindow
                        if (win) {
                          win.postMessage({ type: "BOLD_THEME_CHANGED", payload: nextTheme, theme: nextTheme }, "*")
                          win.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: nextTheme, theme: nextTheme }, "*")
                          win.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: { theme: nextTheme, language: currentLang }, theme: nextTheme, language: currentLang, lang: currentLang }, "*")
                          win.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: { theme: nextTheme, language: currentLang }, theme: nextTheme, language: currentLang, lang: currentLang }, "*")
                          win.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: { theme: nextTheme, language: currentLang }, theme: nextTheme, language: currentLang, lang: currentLang }, "*")
                          win.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: { theme: nextTheme, language: currentLang }, theme: nextTheme, language: currentLang, lang: currentLang }, "*")
                        }
                      } catch (e) { }
                    })
                  } catch (e) { }
                }}
                className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${theme === "dark" ? "bg-[#004EB1]" : "bg-zinc-200 dark:bg-zinc-850"
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block size-3 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${theme === "dark" ? "translate-x-3" : "translate-x-0"
                    }`}
                />
              </button>
            </div>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer text-xs py-1.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2 font-sans font-semibold">
                <Languages className="size-4 text-[#004EB1] dark:text-blue-400" />
                <span>{t.languageLabel || "Idioma"} ({lang.toUpperCase()})</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="w-36 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-1 rounded-md font-sans shadow-lg z-[99999]">
                  <DropdownMenuItem
                    onClick={() => { setLang("pt"); localStorage.setItem("language", "pt"); }}
                    className={`cursor-pointer text-xs py-1.5 rounded-md flex items-center gap-1.5 focus:bg-zinc-50 dark:focus:bg-zinc-800 ${lang === "pt" ? "text-[#004EB1] font-bold" : ""}`}
                  >
                    <span>🇧🇷 Português</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setLang("en"); localStorage.setItem("language", "en"); }}
                    className={`cursor-pointer text-xs py-1.5 rounded-md flex items-center gap-1.5 focus:bg-zinc-50 dark:focus:bg-zinc-800 ${lang === "en" ? "text-[#004EB1] font-bold" : ""}`}
                  >
                    <span>🇺🇸 English</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setLang("es"); localStorage.setItem("language", "es"); }}
                    className={`cursor-pointer text-xs py-1.5 rounded-md flex items-center gap-1.5 focus:bg-zinc-50 dark:focus:bg-zinc-800 ${lang === "es" ? "text-[#004EB1] font-bold" : ""}`}
                  >
                    <span>🇪🇸 Español</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSeparator className="bg-zinc-100 dark:bg-zinc-800" />

            <DropdownMenuItem
              onClick={handleLogout}
              className="text-red-600 focus:bg-red-50 dark:focus:bg-red-955/20 cursor-pointer text-xs py-1.5 rounded-md flex items-center gap-2 font-sans font-semibold"
            >
              <LogOut className="size-4" />
              {t.logoutTitle || "Sair do Sistema"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
